import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { UseMutationOptions, useQueryClient } from 'react-query';
import http from '../config/http-service';
import type { RestaurantInquiryItem, UpdateRestaurantInquiryItemStatusPayload } from './types';
import { restaurantInquiriesEndpoint } from './use-create-restaurant-inquiry-api';

// PATCH ums/v1/cms/restaurant-inquiries/:id/items/:itemId/status — per-item
// lifecycle transition. 400 once the item is FULFILLED/UNFULFILLED (terminal).
// No cascade: setting every item FULFILLED does NOT flip the parent inquiry —
// that's a separate PATCH :id/status call.
type TData = RestaurantInquiryItem;
type TError = AxiosError;
type TVariables = { id: number | string; itemId: number } & UpdateRestaurantInquiryItemStatusPayload;

type Options = Omit<UseMutationOptions<TData, TError, TVariables>, 'mutationFn'>;
type TReturnType = ReturnType<typeof useMutationWrapper<TData, TError, TVariables>>;

export function useUpdateRestaurantInquiryItemStatusApi(options?: Options): TReturnType {
  const queryClient = useQueryClient();
  const { onSuccess: userOnSuccess, ...restOptions } = options || {};

  const mutationFn = async ({ id, itemId, status, reason }: TVariables): Promise<TData> => {
    const response = await http.patch(`${restaurantInquiriesEndpoint}/${id}/items/${itemId}/status`, {
      status,
      reason,
    });
    return response.data?.data ?? response.data;
  };

  return useMutationWrapper(mutationFn, {
    mutationKey: [restaurantInquiriesEndpoint, 'update-item-status'],
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries([restaurantInquiriesEndpoint]);
      userOnSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
}
